import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { GradingScale, TermStructure, ResidencyStatus } from '@gpa-scales/shared-types';
import type { AppSettings } from '@gpa-scales/shared-types';
import { HomeschoolMode } from './HomeschoolMode';
import { CustomWeightingEditor } from './CustomWeightingEditor';
import { Select } from 'calc-controls';
import { HelpTip } from './Tooltip';

export function SettingsBar({ onOpenAbout }: { onOpenAbout: () => void }) {
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);

  const apply = (updates: Partial<AppSettings>) => {
    updateSettings(updates);
    recalculateStore();
  };

  // Rendered inside <SettingsDrawer>, which controls visibility and provides
  // padding/background, so no open/close gate or outer panel wrapper is needed.

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="grading-scale" className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]" title="Controls the scale used to interpret and report grades.">
            Grading Scale
          </label>
          <Select
            inline
            label="Grading Scale"
            value={settings.gradingScale}
            onChange={(v) => apply({ gradingScale: v as GradingScale })}
            options={[
              { value: GradingScale.Scale4, label: '4.0 Scale' },
              { value: GradingScale.Scale5, label: '5.0 Scale' },
              { value: GradingScale.Scale100, label: '100-Point' },
            ]}
          />
        </div>

        <div>
          <label htmlFor="term-structure" className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]" title="Sets the period structure used when grouping courses in the term breakdown.">
            Term Structure
          </label>
          <Select
            inline
            label="Term Structure"
            value={settings.termStructure}
            onChange={(v) => apply({ termStructure: v as TermStructure })}
            options={[
              { value: TermStructure.Semester, label: 'Semester' },
              { value: TermStructure.Quarter, label: 'Quarter' },
              { value: TermStructure.Trimester, label: 'Trimester' },
            ]}
          />
        </div>

        <div>
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-[var(--color-text-secondary)]">
            <label htmlFor="residency">Residency</label>
            <HelpTip
              content={'Sets UC eligibility: California residents need a 3.0 UC Capped GPA; non-residents need 3.4. Non-residents earn the UC honors bonus from AP and IB courses only.'}
              label="About residency"
              placement="bottom"
            />
          </div>
          <Select
            inline
            label="Residency"
            value={settings.residencyStatus}
            onChange={(v) => apply({ residencyStatus: v as ResidencyStatus })}
            options={[
              { value: ResidencyStatus.CaliforniaResident, label: 'CA Resident' },
              { value: ResidencyStatus.NonResident, label: 'Non-Resident' },
            ]}
          />
        </div>

        <div className="flex items-end gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="plusMinus"
              checked={settings.plusMinusEnabled}
              onChange={(e) => apply({ plusMinusEnabled: e.target.checked })}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="plusMinus" className="text-sm text-[var(--color-text-secondary)]" title="When on, letter grades like A- or B+ map to distinct grade points (e.g. A- = 3.7).">
              +/- Grades
            </label>
          </div>
        </div>
      </div>

      {/* Phase 3: Homeschool Mode & Custom Weighting */}
      <div className="grid gap-4">
        <HomeschoolMode />
        <CustomWeightingEditor />
      </div>

      {/* Demo data controls */}
      <DemoDataControls />

      {/* About — app identity modal trigger (last item) */}
      <div className="border-t border-border pt-4">
        <button
          onClick={onOpenAbout}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:border-primary hover:text-primary"
        >
          About
        </button>
      </div>
    </div>
  );
}

function DemoDataControls() {
  const showLoadDemo = useAppStore((s) => s.showLoadDemo);
  const setShowLoadDemo = useAppStore((s) => s.setShowLoadDemo);
  const clearDemoData = useAppStore((s) => s.clearDemoData);
  const resetSettings = useAppStore((s) => s.resetSettings);

  const handleClear = () => {
    clearDemoData();
    resetSettings();
    recalculateStore();
  };

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        Demo data
      </h3>

      <div className="flex items-center justify-between gap-4">
        <div>
          <label htmlFor="showLoadDemo" className="text-sm text-[var(--color-text-secondary)]">
            Show &ldquo;Load Demo&rdquo; option
          </label>
          <p className="text-xs text-[var(--color-text-muted)]">
            Show or hide the demo dataset link in the header.
          </p>
        </div>
        <input
          type="checkbox"
          id="showLoadDemo"
          checked={showLoadDemo}
          onChange={(e) => setShowLoadDemo(e.target.checked)}
          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
        />
      </div>

      <button
        onClick={handleClear}
        title="Removes all courses and resets grading, residency, and weighting settings back to their defaults."
        className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:border-error hover:text-error"
      >
        Clear all data and reset settings
      </button>
    </div>
  );
}
